import { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { TrendingUp, TrendingDown, Minus, Sparkles } from "lucide-react";
import { api, timeAgo } from "../lib/api";
import Pager from "./Pager";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const fmtN = (n) => (n ?? 0).toLocaleString("en-GB");
const fmtDT = (d) => d ? new Date(d).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—";
const fmtD = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";

const VIEWS = [["live", "Live now"], ["log", "Visit log"], ["trends", "Trends"], ["breakdowns", "Breakdowns"]];
const RANGES = [["today", "Today"], ["7d", "7 days"], ["30d", "30 days"], ["month", "This month"], ["year", "This year"], ["all", "All time"]];
const RANGE_LABEL = { today: "today", "7d": "last 7 days", "30d": "last 30 days", month: "this month", year: "this year", all: "all time" };
const LOG_PERIODS = [["", "All time"], ["today", "Today"], ["7d", "7 days"], ["30d", "30 days"]];
const BD_TABS = [["jobs", "Jobs"], ["pages", "Pages"], ["landing", "Landing pages"], ["referrers", "Referrers"], ["sources", "Sources"], ["countries", "Countries"], ["cities", "Towns & cities"], ["devices", "Devices"]];
const DEV_COLORS = ["bg-blue-600", "bg-amber-500", "bg-violet-500", "bg-emerald-500", "bg-zinc-400"];

function Delta({ cur, prev, label }) {
  if (!prev) return null;
  const pct = Math.round(((cur - prev) / prev) * 100);
  if (pct === 0) return <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-zinc-400" title={label}><Minus className="h-3 w-3" />0%</span>;
  const up = pct > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${up ? "text-green-600" : "text-red-500"}`} title={label}>
      {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{Math.abs(pct)}%
    </span>
  );
}

function Kpi({ label, value, sub, testid, live, delta }) {
  return (
    <div className="border border-zinc-200 rounded-md px-3.5 py-3 bg-white" data-testid={testid}>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 flex items-center gap-1.5">
        {live && <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" /></span>}
        {label}
      </p>
      <p className="font-display text-xl font-black text-zinc-950 mt-0.5 tabular-nums flex items-center gap-1.5">{value}{delta}</p>
      {sub && <p className="text-[10px] text-zinc-400 mt-0.5 truncate">{sub}</p>}
    </div>
  );
}

function Who({ v }) {
  if (v.name) {
    return (
      <div className="min-w-0 leading-tight">
        <p className="text-xs font-semibold text-zinc-950 truncate">{v.name}</p>
        <p className="text-[10px] text-zinc-400 truncate">{v.email}</p>
      </div>
    );
  }
  return <span className="text-xs text-zinc-400">Guest</span>;
}

function BarRow({ label, sub, value, max, testid }) {
  return (
    <div className="group py-1" data-testid={testid} title={`${label}${sub ? `, ${sub}` : ""}: ${fmtN(value)}`}>
      <div className="flex justify-between text-xs mb-0.5">
        <span className="font-medium text-zinc-800 truncate">{label}</span>
        <span className="text-zinc-500 shrink-0 ml-2 tabular-nums">{fmtN(value)}</span>
      </div>
      <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
        <div className="h-full bg-blue-600 rounded-full transition-all duration-500 group-hover:bg-blue-500"
          style={{ width: `${max ? Math.max(3, (value / max) * 100) : 0}%` }} />
      </div>
      {sub && <p className="text-[10px] text-zinc-400 mt-0.5 truncate">{sub}</p>}
    </div>
  );
}

const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-zinc-950 text-white text-xs rounded-md px-3 py-2 shadow-lg">
      <p className="font-semibold mb-1">{new Date(label + "T00:00:00Z").toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</p>
      {payload.map((p) => <p key={p.dataKey} style={{ color: p.color }}>{p.name}: {fmtN(p.value)}</p>)}
    </div>
  );
};

export default function LiveTraffic() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [view, setView] = useState("live");
  const [range, setRange] = useState("30d");
  const [bdTab, setBdTab] = useState("jobs");
  const [liveIdentified, setLiveIdentified] = useState(false);
  const [visits, setVisits] = useState(null);
  const [visitsTotal, setVisitsTotal] = useState(0);
  const [visitsPage, setVisitsPage] = useState(1);
  const [countries, setCountries] = useState([]);
  const [vf, setVf] = useState({ q: "", country: "", source: "", device: "", identified: false, period: "" });
  const [visitor, setVisitor] = useState(undefined); // undefined = closed, null = loading

  const load = (r = range) => api.get("/admin/traffic/live", { params: { range: r } }).then((res) => { setData(res.data); setErr(false); }).catch(() => setErr(true));
  const loadVisits = (page = visitsPage, f = vf) => {
    const day0 = new Date(); day0.setHours(0, 0, 0, 0);
    const date_from = f.period === "today" ? day0.toISOString().slice(0, 10)
      : f.period === "7d" ? new Date(Date.now() - 7 * 864e5).toISOString().slice(0, 10)
      : f.period === "30d" ? new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10) : "";
    api.get("/admin/traffic/visits", {
      params: { page, limit: 25, q: f.q, country: f.country, source: f.source, device: f.device, identified: f.identified ? "1" : "", date_from },
    }).then((r) => {
      setVisits(r.data.items || []); setVisitsTotal(r.data.total || 0); setVisitsPage(r.data.page || 1);
      if (r.data.filters?.countries?.length) setCountries(r.data.filters.countries);
    }).catch(() => {});
  };
  const openVisitor = (v) => {
    if (!v.visitor) return;
    setVisitor(null);
    api.get(`/admin/traffic/visitor/${v.visitor}`).then((r) => setVisitor(r.data)).catch(() => setVisitor(undefined));
  };
  useEffect(() => {
    load(); loadVisits(1);
    const id = setInterval(() => load(), 10000);
    return () => clearInterval(id);
  }, []);
  const changeRange = (r) => { setRange(r); load(r); };

  if (err && !data) return <p className="border border-red-200 bg-red-50/50 rounded-md p-10 text-center text-sm text-red-700" data-testid="livetraffic-error">Couldn't load traffic data, refresh to try again.</p>;
  if (!data) return <div className="h-64 rounded-md bg-zinc-50 animate-pulse" data-testid="livetraffic-loading" />;

  const bdData = {
    pages: (data.top_pages || []).map((p) => ({ label: p.path, sub: `${fmtN(p.visitors)} unique visitors`, value: p.views, testid: `lt-page-${p.path.replace(/[^a-z0-9]/gi, "-")}` })),
    landing: (data.top_landing || []).map((p) => ({ label: p.path, sub: "first page visitors arrived on", value: p.visitors, testid: `lt-landing-${p.path.replace(/[^a-z0-9]/gi, "-")}` })),
    referrers: (data.top_referrers || []).map((r) => ({ label: r.host, sub: "sent visitors here", value: r.visits, testid: `lt-referrer-${r.host.replace(/[^a-z0-9]/gi, "-")}` })),
    sources: (data.sources || []).map((s) => ({ label: s.source, sub: "", value: s.visits, testid: `lt-source-${s.source.toLowerCase().replace(/[^a-z0-9]/g, "-")}` })),
    countries: (data.top_countries || []).map((c) => ({ label: c.country, sub: c.code, value: c.visitors, testid: `lt-country-${c.country.toLowerCase().replace(/[^a-z0-9]/g, "-")}` })),
    cities: (data.top_cities || []).map((c) => ({ label: c.city, sub: c.country, value: c.visitors, testid: `lt-city-${c.city.toLowerCase().replace(/[^a-z0-9]/g, "-")}` })),
  };
  const bdRows = bdData[bdTab] || [];
  const bdMax = Math.max(1, ...bdRows.map((r) => r.value));
  const totalDevice = Math.max(1, (data.devices || []).reduce((a, d) => a + d.visits, 0));
  const liveRows = liveIdentified ? data.live.filter((v) => v.name) : data.live;
  const jobs = data.top_jobs || [];
  const maxJobViews = Math.max(1, ...jobs.map((j) => j.views));

  return (
    <div className="space-y-4" data-testid="livetraffic">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2" data-testid="livetraffic-kpis">
        <Kpi live testid="lt-live" label="On the site now" value={fmtN(data.live_now)} sub="last 3 min · auto 10s" />
        <Kpi testid="lt-today-visitors" label="Visitors today" value={fmtN(data.today.visitors)} sub={`${fmtN(data.today.views)} views`}
          delta={<Delta cur={data.today.visitors} prev={data.yesterday?.visitors} label={`vs yesterday (${fmtN(data.yesterday?.visitors)})`} />} />
        <Kpi testid="lt-30d-visitors" label="Visitors · 30d" value={fmtN(data.last_30d.visitors)} sub={`${fmtN(data.last_30d.views)} views`}
          delta={<Delta cur={data.last_30d.visitors} prev={data.prev_30d?.visitors} label={`vs previous 30 days (${fmtN(data.prev_30d?.visitors)})`} />} />
        <Kpi testid="lt-total" label="All-time" value={fmtN(data.visitors_total)} sub="unique visitors" />
        <Kpi testid="lt-avg" label="Views / visit · 30d" value={data.last_30d.visitors ? (data.last_30d.views / data.last_30d.visitors).toFixed(1) : "0"} sub="engagement depth" />
      </div>

      {data.insights && (data.insights.peak_hour !== null && data.insights.peak_hour !== undefined || data.insights.busiest_day) && (
        <div className="flex flex-wrap items-center gap-2" data-testid="lt-insights">
          <Sparkles className="h-3.5 w-3.5 text-amber-500 shrink-0" />
          {data.insights.peak_hour !== null && data.insights.peak_hour !== undefined && (
            <span className="text-[11px] font-medium text-zinc-600 bg-white border border-zinc-200 rounded-full px-3 py-1" data-testid="lt-insight-peak"
              title="The hour of day with the most page views over the last 30 days">
              Peak traffic {`${data.insights.peak_hour}:00–${(data.insights.peak_hour + 1) % 24}:00`}
            </span>
          )}
          {data.insights.busiest_day && (
            <span className="text-[11px] font-medium text-zinc-600 bg-white border border-zinc-200 rounded-full px-3 py-1" data-testid="lt-insight-day"
              title="Your single busiest day in the last 30 days">
              Busiest day {new Date(data.insights.busiest_day.date + "T00:00:00Z").toLocaleDateString("en-GB", { day: "numeric", month: "short" })} · {fmtN(data.insights.busiest_day.views)} views
            </span>
          )}
          <span className="text-[11px] font-medium text-zinc-600 bg-white border border-zinc-200 rounded-full px-3 py-1 inline-flex items-center gap-1" data-testid="lt-insight-week"
            title="Page views in the last 7 days vs the 7 days before">
            This week {fmtN(data.insights.week_views)} views
            <Delta cur={data.insights.week_views} prev={data.insights.prev_week_views} label={`vs previous week (${fmtN(data.insights.prev_week_views)})`} />
          </span>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 border border-zinc-200 rounded-md bg-white p-1" data-testid="lt-views">
          {VIEWS.map(([id, label]) => (
            <button key={id} onClick={() => setView(id)} data-testid={`lt-view-${id}`}
              className={`h-8 px-4 text-xs font-semibold rounded transition-colors duration-200 ${view === id ? "bg-zinc-950 text-white" : "text-zinc-500 hover:text-zinc-900"}`}>
              {label}
              {id === "live" && data.live_now > 0 && <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-green-500 text-white text-[10px] font-bold">{data.live_now}</span>}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 border border-zinc-200 rounded-md bg-white p-1 ml-auto" data-testid="lt-ranges" title="Date range for Trends & Breakdowns">
          {RANGES.map(([id, label]) => (
            <button key={id} onClick={() => changeRange(id)} data-testid={`lt-range-${id}`}
              className={`h-7 px-2.5 text-[11px] font-semibold rounded transition-colors duration-200 ${range === id ? "bg-blue-600 text-white" : "text-zinc-500 hover:text-zinc-900"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {view === "live" && (
        <div className="border border-zinc-200 rounded-md bg-white overflow-hidden" data-testid="lt-live-panel">
          <div className="px-4 py-2.5 border-b border-zinc-100 flex items-center justify-between gap-3 flex-wrap">
            <p className="font-display font-bold text-zinc-950 text-sm flex items-center gap-2">
              <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" /></span>
              Who's on the site right now
            </p>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-[11px] font-medium text-zinc-500 cursor-pointer">
                <input type="checkbox" checked={liveIdentified} onChange={(e) => setLiveIdentified(e.target.checked)}
                  data-testid="lt-live-identified" className="h-3.5 w-3.5 accent-zinc-900" />
                Signed-in only
              </label>
              <span className="text-[10px] text-zinc-400">click a visitor for their full journey</span>
            </div>
          </div>
          {liveRows.length === 0 ? (
            <p className="px-5 py-8 text-sm text-zinc-500 text-center" data-testid="lt-live-empty">
              {data.live.length === 0
                ? "No one is browsing at this exact moment, this panel fills in live as visitors arrive. Signed-in visitors appear with name and email. IP addresses are never stored."
                : "No signed-in visitors right now."}
            </p>
          ) : (
            <div className="h-[420px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-400 border-b border-zinc-100">
                    <th className="px-4 py-2 font-semibold">Who</th>
                    <th className="px-3 py-2 font-semibold">Location</th>
                    <th className="px-3 py-2 font-semibold">On page, right now</th>
                    <th className="px-3 py-2 font-semibold">Device</th>
                    <th className="px-3 py-2 font-semibold">Source</th>
                    <th className="px-3 py-2 font-semibold">Visit</th>
                    <th className="px-3 py-2 font-semibold">Active</th>
                  </tr>
                </thead>
                <tbody>
                  {liveRows.map((v, i) => (
                    <tr key={v.visitor} onClick={() => openVisitor(v)} className="border-b border-zinc-50 hover:bg-blue-50/40 cursor-pointer transition-colors duration-150" data-testid={`lt-live-row-${i}`} title="Open visitor journey">
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <Who v={v} />
                          {v.name && <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-blue-50 text-blue-700 shrink-0">Signed in</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-zinc-600 whitespace-nowrap">
                        {v.city ? `${v.city}, ` : ""}{v.country || "Pending"}
                        {v.country_code && <span className="ml-1 text-[10px] font-bold text-zinc-400 uppercase">{v.country_code}</span>}
                      </td>
                      <td className="px-3 py-2 text-xs font-medium text-zinc-800 max-w-[220px] truncate" title={v.path}>{v.path || "/"}</td>
                      <td className="px-3 py-2 text-xs text-zinc-500 whitespace-nowrap">{v.device || "—"}</td>
                      <td className="px-3 py-2"><span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 whitespace-nowrap">{v.source || "Direct"}</span></td>
                      <td className="px-3 py-2"><span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${v.returning ? "bg-violet-50 text-violet-700" : "bg-green-50 text-green-700"}`}>{v.returning ? "Returning" : "New"}</span></td>
                      <td className="px-3 py-2 text-[11px] text-zinc-400 whitespace-nowrap" title="Last activity">{v.last_seen ? timeAgo(v.last_seen) : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {view === "log" && (
        <div className="border border-zinc-200 rounded-md bg-white overflow-hidden" data-testid="lt-visits">
          <div className="px-4 py-2.5 border-b border-zinc-100">
            <p className="font-display font-bold text-zinc-950 text-sm">
              Every visit, exact time <span className="text-[11px] font-normal text-zinc-400 ml-1">{fmtN(visitsTotal)} recorded · 25 per page</span>
            </p>
          </div>
          <div className="px-4 py-2.5 border-b border-zinc-100 flex flex-col lg:flex-row gap-2" data-testid="lt-visits-filters">
            <input value={vf.q} onChange={(e) => setVf({ ...vf, q: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter") loadVisits(1); }}
              placeholder="Search page, name, email or town" data-testid="lt-filter-q"
              className="flex-1 h-8 px-3 border border-zinc-300 rounded-md text-xs" />
            <select value={vf.period} onChange={(e) => { const f = { ...vf, period: e.target.value }; setVf(f); loadVisits(1, f); }}
              data-testid="lt-filter-period" className="lg:w-32 h-8 px-2 border border-zinc-300 rounded-md text-xs bg-white">
              {LOG_PERIODS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
            </select>
            <select value={vf.country} onChange={(e) => { const f = { ...vf, country: e.target.value }; setVf(f); loadVisits(1, f); }}
              data-testid="lt-filter-country" className="lg:w-40 h-8 px-2 border border-zinc-300 rounded-md text-xs bg-white">
              <option value="">All countries</option>
              {countries.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={vf.source} onChange={(e) => { const f = { ...vf, source: e.target.value }; setVf(f); loadVisits(1, f); }}
              data-testid="lt-filter-source" className="lg:w-36 h-8 px-2 border border-zinc-300 rounded-md text-xs bg-white">
              <option value="">All sources</option>
              {(data.sources || []).map((s) => <option key={s.source} value={s.source}>{s.source}</option>)}
            </select>
            <select value={vf.device} onChange={(e) => { const f = { ...vf, device: e.target.value }; setVf(f); loadVisits(1, f); }}
              data-testid="lt-filter-device" className="lg:w-32 h-8 px-2 border border-zinc-300 rounded-md text-xs bg-white">
              <option value="">All devices</option>
              {(data.devices || []).map((d) => <option key={d.device} value={d.device}>{d.device}</option>)}
            </select>
            <label className="flex items-center gap-2 text-[11px] font-medium text-zinc-500 cursor-pointer whitespace-nowrap">
              <input type="checkbox" checked={vf.identified} onChange={(e) => { const f = { ...vf, identified: e.target.checked }; setVf(f); loadVisits(1, f); }}
                data-testid="lt-filter-identified" className="h-3.5 w-3.5 accent-zinc-900" />
              Signed-in only
            </label>
            <button onClick={() => loadVisits(1)} data-testid="lt-filter-apply"
              className="h-8 px-4 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200">Apply</button>
          </div>
          {!visits ? (
            <div className="h-40 animate-pulse bg-zinc-50" />
          ) : visits.length === 0 ? (
            <p className="px-5 py-8 text-sm text-zinc-500 text-center" data-testid="lt-visits-empty">No visits match these filters.</p>
          ) : (
            <div className="h-[460px] overflow-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-400 border-b border-zinc-100">
                    <th className="px-4 py-2 font-semibold">Exact time</th>
                    <th className="px-3 py-2 font-semibold">Who</th>
                    <th className="px-3 py-2 font-semibold">Location</th>
                    <th className="px-3 py-2 font-semibold">Page</th>
                    <th className="px-3 py-2 font-semibold">Device</th>
                    <th className="px-3 py-2 font-semibold">Source</th>
                    <th className="px-3 py-2 font-semibold">Visit</th>
                  </tr>
                </thead>
                <tbody>
                  {visits.map((v, i) => (
                    <tr key={`${v.visitor}-${v.at}`} onClick={() => openVisitor(v)} className="border-b border-zinc-50 hover:bg-blue-50/40 cursor-pointer transition-colors duration-150" data-testid={`lt-visit-row-${i}`} title="Open visitor journey">
                      <td className="px-4 py-2 text-xs text-zinc-500 whitespace-nowrap tabular-nums" data-testid={`lt-visit-time-${i}`}>{fmtDT(v.at)}</td>
                      <td className="px-3 py-2"><Who v={v} /></td>
                      <td className="px-3 py-2 text-xs text-zinc-600 whitespace-nowrap">
                        {v.city ? `${v.city}, ` : ""}{v.country || "—"}
                        {v.country_code && <span className="ml-1 text-[10px] font-bold text-zinc-400 uppercase">{v.country_code}</span>}
                      </td>
                      <td className="px-3 py-2 text-xs font-medium text-zinc-800 max-w-[220px] truncate" title={v.path}>{v.path || "/"}</td>
                      <td className="px-3 py-2 text-xs text-zinc-500 whitespace-nowrap">{v.device || "—"}</td>
                      <td className="px-3 py-2"><span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 whitespace-nowrap">{v.source || "Direct"}</span></td>
                      <td className="px-3 py-2"><span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${v.returning ? "bg-violet-50 text-violet-700" : "bg-green-50 text-green-700"}`}>{v.returning ? "Returning" : "New"}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="px-4">
            <Pager page={visitsPage} total={visitsTotal} onChange={(p) => loadVisits(p)} testid="lt-visits-pager" />
          </div>
        </div>
      )}

      {view === "trends" && (
        <div className="border border-zinc-200 rounded-md p-5 bg-white" data-testid="lt-chart">
          <p className="font-display font-bold text-zinc-950 mb-4">Daily traffic, {RANGE_LABEL[range]}</p>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={data.series || []} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
              <defs>
                <linearGradient id="ltViews" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563eb" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#2563eb" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="ltVisitors" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#f59e0b" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#f4f4f5" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#a1a1aa" }} tickLine={false} axisLine={false}
                tickFormatter={(d) => new Date(d + "T00:00:00Z").toLocaleDateString("en-GB", { day: "numeric", month: "short" })} />
              <YAxis tick={{ fontSize: 10, fill: "#a1a1aa" }} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip content={<ChartTip />} />
              <Area type="monotone" dataKey="views" name="Page views" stroke="#2563eb" strokeWidth={2} fill="url(#ltViews)" />
              <Area type="monotone" dataKey="visitors" name="Visitors" stroke="#f59e0b" strokeWidth={2} fill="url(#ltVisitors)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {view === "breakdowns" && (
        <div className="border border-zinc-200 rounded-md bg-white overflow-hidden" data-testid="lt-breakdowns">
          <div className="px-4 py-2 border-b border-zinc-100 flex items-center gap-1 flex-wrap" data-testid="lt-bd-tabs">
            {BD_TABS.map(([id, label]) => (
              <button key={id} onClick={() => setBdTab(id)} data-testid={`lt-bd-tab-${id}`}
                className={`h-7 px-3 text-[11px] font-semibold rounded transition-colors duration-200 ${bdTab === id ? "bg-zinc-950 text-white" : "text-zinc-500 hover:text-zinc-900"}`}>
                {label}
              </button>
            ))}
            <span className="ml-auto text-[10px] text-zinc-400">{RANGE_LABEL[range]} · top 12 each</span>
          </div>
          <div className="p-4 h-[420px] overflow-y-auto" data-testid="lt-bd-body">
            {bdTab === "devices" ? (
              <div className="space-y-4 pt-2">
                <div className="flex h-4 rounded-full overflow-hidden bg-zinc-100" data-testid="lt-devices-bar">
                  {(data.devices || []).map((d, i) => (
                    <div key={d.device} className={`${DEV_COLORS[i % 5]} h-full`}
                      title={`${d.device}: ${fmtN(d.visits)} visits (${Math.round((d.visits / totalDevice) * 100)}%)`}
                      style={{ width: `${(d.visits / totalDevice) * 100}%` }} />
                  ))}
                </div>
                <div className="space-y-2">
                  {(data.devices || []).map((d, i) => (
                    <BarRow key={d.device} testid={`lt-device-${(d.device || "other").toLowerCase()}`} label={d.device}
                      sub={`${Math.round((d.visits / totalDevice) * 100)}% of visits`} value={d.visits}
                      max={Math.max(1, ...(data.devices || []).map((x) => x.visits))} />
                  ))}
                </div>
              </div>
            ) : bdTab === "jobs" ? (
              jobs.length === 0 ? (
                <p className="text-xs text-zinc-400 py-10 text-center" data-testid="lt-jobs-empty">No job pages viewed in this period yet.</p>
              ) : (
                <table className="w-full text-sm" data-testid="lt-jobs-table">
                  <thead className="sticky top-0 bg-white z-10">
                    <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-400 border-b border-zinc-100">
                      <th className="px-2 py-2 font-semibold">Job</th>
                      <th className="px-3 py-2 font-semibold">Views</th>
                      <th className="px-3 py-2 font-semibold">Applications</th>
                      <th className="px-3 py-2 font-semibold">Apply rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map((j, i) => (
                      <tr key={j.ref + i} className="border-b border-zinc-50 hover:bg-zinc-50/60 transition-colors duration-150" data-testid={`lt-job-row-${i}`}>
                        <td className="px-2 py-2">
                          <p className="text-xs font-semibold text-zinc-900 truncate max-w-[300px]" title={j.title}>{j.title}</p>
                          <p className="text-[10px] text-zinc-400 truncate">{j.company || (j.external ? "Partner listing" : "")}{j.external ? " · partner" : ""}</p>
                          <div className="h-1 bg-zinc-100 rounded-full overflow-hidden mt-1 max-w-[220px]">
                            <div className="h-full bg-blue-600 rounded-full" style={{ width: `${Math.max(2, (j.views / maxJobViews) * 100)}%` }} />
                          </div>
                        </td>
                        <td className="px-3 py-2 text-xs text-zinc-700 tabular-nums whitespace-nowrap">{fmtN(j.views)}</td>
                        <td className="px-3 py-2 text-xs text-zinc-700 tabular-nums whitespace-nowrap">{j.external ? "outbound" : fmtN(j.applications)}</td>
                        <td className="px-3 py-2 text-xs whitespace-nowrap">
                          {j.external ? <span className="text-zinc-400">—</span> : (
                            <span className={`font-semibold ${j.views && (j.applications / j.views) >= 0.05 ? "text-green-600" : "text-zinc-500"}`}>
                              {j.views ? `${((j.applications / j.views) * 100).toFixed(1)}%` : "—"}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            ) : bdRows.length === 0 ? (
              <p className="text-xs text-zinc-400 py-10 text-center" data-testid="lt-bd-empty">
                {bdTab === "countries" || bdTab === "cities"
                  ? "Location data builds up as new visitors arrive, geo is resolved per visit and the IP itself is never stored."
                  : bdTab === "referrers" ? "No external referrers recorded in this period yet, this fills in as people arrive from Google, social, email links…"
                  : bdTab === "landing" ? "Collecting…" : "Collecting…"}
              </p>
            ) : (
              <div className="divide-y divide-zinc-50">
                {bdRows.map((r) => <BarRow key={r.label} testid={r.testid} label={r.label} sub={r.sub} value={r.value} max={bdMax} />)}
              </div>
            )}
          </div>
        </div>
      )}

      <Dialog open={visitor !== undefined} onOpenChange={() => setVisitor(undefined)}>
        <DialogContent className="bg-white sm:max-w-lg" data-testid="lt-visitor-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold">
              {visitor?.name || "Guest visitor"}
            </DialogTitle>
          </DialogHeader>
          {visitor === null ? (
            <div className="h-40 animate-pulse bg-zinc-50 rounded-md" data-testid="lt-visitor-loading" />
          ) : visitor && (
            <div className="space-y-4" data-testid="lt-visitor-body">
              <div className="flex flex-wrap gap-1.5">
                {visitor.email && <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-zinc-100 text-zinc-700" data-testid="lt-visitor-email">{visitor.email}</span>}
                {visitor.role && <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-blue-50 text-blue-700 capitalize">{visitor.role}</span>}
                {visitor.company_name && <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-zinc-100 text-zinc-700">{visitor.company_name}</span>}
                {visitor.jobs_posted !== undefined && <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-violet-50 text-violet-700">{visitor.jobs_posted} jobs posted</span>}
                {visitor.applications !== undefined && <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-green-50 text-green-700">{visitor.applications} applications</span>}
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-zinc-600" data-testid="lt-visitor-meta">
                <p><span className="font-semibold text-zinc-800">Location:</span> {visitor.city ? `${visitor.city}, ` : ""}{visitor.country || "—"}</p>
                <p><span className="font-semibold text-zinc-800">Device:</span> {visitor.device || "—"}</p>
                <p><span className="font-semibold text-zinc-800">Arrived via:</span> {visitor.ref_host || visitor.source || "Direct"}</p>
                <p><span className="font-semibold text-zinc-800">Sessions:</span> {fmtN(visitor.sessions)}</p>
                <p><span className="font-semibold text-zinc-800">Total page views:</span> {fmtN(visitor.views_total)}</p>
                <p><span className="font-semibold text-zinc-800">First seen:</span> {fmtD(visitor.first_seen)}</p>
                <p><span className="font-semibold text-zinc-800">Last active:</span> {visitor.last_seen ? timeAgo(visitor.last_seen) : "—"}</p>
                {visitor.joined && <p><span className="font-semibold text-zinc-800">Member since:</span> {fmtD(visitor.joined)}</p>}
              </div>
              <div>
                <p className="text-xs font-semibold text-zinc-500 mb-2">Recent journey (last {visitor.history.length} pages)</p>
                <div className="max-h-56 overflow-y-auto border border-zinc-100 rounded-md" data-testid="lt-visitor-history">
                  {visitor.history.map((h, i) => (
                    <div key={`${h.at}-${h.path}`} className={`px-3 py-1.5 flex items-center justify-between gap-2 ${i > 0 ? "border-t border-zinc-50" : ""}`} data-testid={`lt-visitor-h-${i}`}>
                      <span className="text-xs font-medium text-zinc-800 truncate" title={h.path}>{h.path || "/"}</span>
                      <span className="text-[10px] text-zinc-400 whitespace-nowrap tabular-nums">{fmtDT(h.at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
